import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import "./ValveTypeStandards.css";

function ValveTypeStandards() {
  const { valveTypeId } = useParams();
  const navigate = useNavigate();
  
  const [valveType, setValveType] = useState(null);
  const [allStandards, setAllStandards] = useState({ size: [], class: [] });
  const [selectedStandards, setSelectedStandards] = useState({ size: [], class: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchData();
  }, [valveTypeId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch valve type details
      const valveResponse = await axios.get(`/valve-types/${valveTypeId}`);
      setValveType(valveResponse.data.data);

      // Fetch all standards from standards master
      const standardsResponse = await axios.get("/standards");
      const allStandardsData = standardsResponse.data;
      
      setAllStandards({
        size: allStandardsData.size || [],
        class: allStandardsData.class || []
      });

      // Fetch currently selected standards for this valve type
      const selectedResponse = await axios.get(`/valve-types/${valveTypeId}/standards`);
      const selectedData = selectedResponse.data.data || [];
      
      // Group selected standards by type
      const selectedByType = {
        size: selectedData.filter(s => s.standard_type === 'size').map(s => s.standard_id),
        class: selectedData.filter(s => s.standard_type === 'class').map(s => s.standard_id)
      };
      
      setSelectedStandards(selectedByType);
    } catch (error) {
      if (error.response?.status === 401) {
        showMessage("error", "Session expired. Please login again.");
      } else if (error.response?.status === 404) {
        showMessage("error", "Valve type not found.");
        setTimeout(() => navigate("/admin/valve-configuration"), 2000);
      } else {
        showMessage("error", error.response?.data?.message || "Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const handleCheckboxChange = (standardType, standardId) => {
    setSelectedStandards(prev => {
      const currentSelection = prev[standardType];
      const isSelected = currentSelection.includes(standardId);
      
      return {
        ...prev,
        [standardType]: isSelected
          ? currentSelection.filter(id => id !== standardId)
          : [...currentSelection, standardId]
      };
    });
  };

  const handleSelectAll = (standardType) => {
    const allIds = allStandards[standardType].map(s => s.id);
    setSelectedStandards(prev => ({
      ...prev,
      [standardType]: allIds
    }));
  };

  const handleDeselectAll = (standardType) => {
    setSelectedStandards(prev => ({
      ...prev,
      [standardType]: []
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Save size standards
      await axios.put(`/valve-types/${valveTypeId}/standards`, {
        standard_type: 'size',
        standard_ids: selectedStandards.size
      });
      
      // Save class standards
      await axios.put(`/valve-types/${valveTypeId}/standards`, {
        standard_type: 'class',
        standard_ids: selectedStandards.class
      });
      
      showMessage("success", "Standards configuration saved successfully");
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate("/admin/valve-configuration");
      }, 1500);
    } catch (error) {
      if (error.response?.status === 401) {
        showMessage("error", "Session expired. Please login again.");
      } else {
        showMessage("error", error.response?.data?.message || "Failed to save standards");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/valve-configuration");
  };

  if (loading) {
    return (
      <div className="valve-standards-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading standards configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="valve-standards-container">
      <div className="standards-header">
        <div>
          <h1 className="standards-title">Configure Standards</h1>
          <p className="standards-subtitle">
            {valveType ? `Valve Type: ${valveType.name}` : "Loading..."}
          </p>
          <p className="standards-description">
            Select which size and class values should be available for this valve type in TCDS
          </p>
        </div>
        <button className="btn-back" onClick={handleCancel}>
          ← Back to Valve Types
        </button>
      </div>

      {message.text && (
        <div className={`alert-message alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="standards-grid">
        {/* Size Standards Section */}
        <div className="standards-section">
          <div className="section-header">
            <h2 className="section-title">Size Standards</h2>
            <div className="section-actions">
              <button 
                className="btn-select-action"
                onClick={() => handleSelectAll('size')}
              >
                Select All
              </button>
              <button 
                className="btn-select-action"
                onClick={() => handleDeselectAll('size')}
              >
                Deselect All
              </button>
            </div>
          </div>
          <div className="section-info">
            <span className="selection-count">
              {selectedStandards.size.length} of {allStandards.size.length} selected
            </span>
          </div>
          <div className="checkboxes-container">
            {allStandards.size.length === 0 ? (
              <p className="no-data-message">No size standards available. Add them in Standards Master.</p>
            ) : (
              allStandards.size.map((standard) => (
                <label key={standard.id} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedStandards.size.includes(standard.id)}
                    onChange={() => handleCheckboxChange('size', standard.id)}
                  />
                  <span className="checkbox-text">{standard.value}</span>
                </label>
              ))
            )}
          </div>
        </div>

        {/* Class Standards Section */}
        <div className="standards-section">
          <div className="section-header">
            <h2 className="section-title">Class Standards</h2>
            <div className="section-actions">
              <button 
                className="btn-select-action"
                onClick={() => handleSelectAll('class')}
              >
                Select All
              </button>
              <button 
                className="btn-select-action"
                onClick={() => handleDeselectAll('class')}
              >
                Deselect All
              </button>
            </div>
          </div>
          <div className="section-info">
            <span className="selection-count">
              {selectedStandards.class.length} of {allStandards.class.length} selected
            </span>
          </div>
          <div className="checkboxes-container">
            {allStandards.class.length === 0 ? (
              <p className="no-data-message">No class standards available. Add them in Standards Master.</p>
            ) : (
              allStandards.class.map((standard) => (
                <label key={standard.id} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedStandards.class.includes(standard.id)}
                    onChange={() => handleCheckboxChange('class', standard.id)}
                  />
                  <span className="checkbox-text">{standard.value}</span>
                </label>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="standards-actions">
        <button 
          className="btn-cancel-action" 
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button 
          className="btn-save-action" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="spinner-small"></span>
              Saving...
            </>
          ) : (
            'Save Configuration'
          )}
        </button>
      </div>

      {/* Summary Info */}
      <div className="standards-summary">
        <div className="summary-card">
          <h3>Configuration Summary</h3>
          <ul>
            <li>
              <strong>Size values selected:</strong> {selectedStandards.size.length}
              {selectedStandards.size.length === 0 && (
                <span className="warning-text"> (No sizes selected - users won't be able to select size in TCDS)</span>
              )}
            </li>
            <li>
              <strong>Class values selected:</strong> {selectedStandards.class.length}
              {selectedStandards.class.length === 0 && (
                <span className="warning-text"> (No classes selected - users won't be able to select class in TCDS)</span>
              )}
            </li>
          </ul>
          <p className="summary-note">
            <strong>Note:</strong> Only selected values will be available in TCDS dropdown menus for this valve type.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ValveTypeStandards;
